import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import { useColumnSettings } from '../../../hooks/useColumnSettings';
import PurchaseOrderItems from './PurchaseOrderItems';
import '../inventory.css';

const PurchaseOrders = ({ organization }) => {
  const { t } = useTranslation();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showItemsModal, setShowItemsModal] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [showColumnMenu, setShowColumnMenu] = useState(null);

  const {
    columnSettings,
    sortConfig,
    toggleColumnVisibility,
    moveColumnUp,
    moveColumnDown,
    handleSort,
    resetColumnSettings,
  } = useColumnSettings('purchaseOrderColumns', organization);

  useEffect(() => {
    if (organization) {
      loadPurchaseOrders();
      loadDropdownData();
    }
  }, [organization, filterStatus]);

  // Generate table columns when purchaseOrders or columnSettings change
  useEffect(() => {
    if (purchaseOrders.length === 0) {
      setTableColumns([]);
      return;
    }
    const firstOrder = purchaseOrders[0];
    const columns = Object.keys(firstOrder)
      .filter(key => key !== 'id' && key !== 'organization')
      .map((key, index) => ({
        key,
        label: formatColumnLabel(key),
        visible: columnSettings[key]?.visible !== false,
        order: columnSettings[key]?.order ?? index,
      }));
    columns.sort((a, b) => a.order - b.order);
    setTableColumns(columns);
  }, [purchaseOrders, columnSettings]);

  const loadDropdownData = async () => {
    try {
      const suppliersResponse = await inventoryApi.getSuppliers(organization);
      setSuppliers(Array.isArray(suppliersResponse) ? suppliersResponse : (suppliersResponse.data || []));
    } catch (err) { /* non-critical */ }
  };

  const loadPurchaseOrders = async () => {
    if (!organization) { setError('Organization is required'); setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getPurchaseOrders(organization, filterStatus || null);
      setPurchaseOrders(Array.isArray(response) ? response : (response.data || []));
    } catch (err) {
      setError(err.message || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    if (!searchTerm) return purchaseOrders;
    const term = searchTerm.toLowerCase();
    return purchaseOrders.filter(order =>
      Object.values(order).some(v => v?.toString().toLowerCase().includes(term))
    );
  }, [purchaseOrders, searchTerm]);

  const sortedOrders = useMemo(() => {
    let sortable = [...filteredOrders];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredOrders, sortConfig]);

  const visibleColumns = tableColumns.filter(col => col.visible);

  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  const formatCellValue = (value, key) => {
    if (value === null || value === undefined) return '-';

    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Format price/cost/total fields
    if ((key.includes('total') || key.includes('amount') || key.includes('price')) && typeof value === 'number') {
      return `$${value.toFixed(2)}`;
    }

    // Format dates
    if (key.includes('date') || key.includes('Date') || key.includes('created') || key.includes('updated')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (e) {
        return value;
      }
    }

    // Format status with color
    if (key === 'status') {
      return value.toString();
    }

    return value.toString();
  };

  // Helper function to determine if field should be a select
  const isSelectField = (key) => {
    return key.endsWith('Id') ||
      key === 'supplierId' ||
      key === 'status' ||
      key === 'paymentTerms';
  };

  // Helper function to get select options
  const getSelectOptions = (key) => {
    if (key === 'supplierId') {
      return suppliers.map(sup => ({ value: sup.id, label: sup.name || sup.supplierName }));
    }
    if (key === 'status') {
      return [
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'ordered', label: 'Ordered' },
        { value: 'received', label: 'Received' },
        { value: 'cancelled', label: 'Cancelled' }
      ];
    }
    if (key === 'paymentTerms') {
      return [
        { value: 'net30', label: 'Net 30' },
        { value: 'net60', label: 'Net 60' },
        { value: 'net90', label: 'Net 90' },
        { value: 'cod', label: 'Cash on Delivery' },
        { value: 'prepaid', label: 'Prepaid' }
      ];
    }
    return [];
  };

  const handleOpenModal = (order = null) => {
    const allCols = Object.keys(purchaseOrders[0] || {}).filter(k => k !== 'id' && k !== 'organization');
    if (order) {
      setEditingId(order.id);
      setEditFormData({ ...order });
    } else {
      setEditingId(null);
      const defaults = { status: 'draft', paymentTerms: 'net30' };
      allCols.forEach(key => { if (!(key in defaults)) defaults[key] = ''; });
      setEditFormData(defaults);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;

    try {
      // Note: Delete endpoint may not be available, using update to set cancelled status
      await inventoryApi.updatePurchaseOrder(orderId, { status: 'cancelled' });
      await loadPurchaseOrders();
    } catch (err) {
      alert('Error deleting purchase order: ' + err.message);
    }
  };

  const handleApprove = async (orderId) => {
    if (!window.confirm('Are you sure you want to approve this purchase order?')) return;

    try {
      const approvedBy = 'current-user'; // TODO: Get from auth context
      await inventoryApi.approvePurchaseOrder(orderId, approvedBy);
      await loadPurchaseOrders();
    } catch (err) {
      alert('Error approving purchase order: ' + err.message);
    }
  };

  const handleSave = async () => {
    try {
      const orderData = {
        ...editFormData,
        organization,
        // Ensure numeric fields are parsed
        totalAmount: parseFloat(editFormData.totalAmount) || 0
      };

      if (editingId) {
        await inventoryApi.updatePurchaseOrder(editingId, orderData);
      } else {
        await inventoryApi.createPurchaseOrder(orderData);
      }

      await loadPurchaseOrders();
      handleCloseModal();
    } catch (err) {
      alert('Error saving purchase order: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_purchase_orders_message')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="pm-page-card">
        <p style={{ color: 'var(--pico-del-color)' }}>
          {t('inventory.error_prefix')} {error}
        </p>
        <button className="pm-btn-primary" onClick={loadPurchaseOrders}>
          {t('inventory.retry_button')}
        </button>
      </div>
    );
  }

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.purchase_orders_title')}</h2>
        <div className="pm-header-actions">
          <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.add_purchase_order_button')}</span>
          </button>
        </div>
      </div>

      <div className="pm-toolbar">
        <div className="pm-search-wrap">
          <input
            type="search"
            placeholder={t('inventory.search_purchase_orders')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-filter-wrap">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">{t('inventory.all_statuses_filter')}</option>
            <option value="draft">{t('inventory.draft_status')}</option>
            <option value="pending">{t('inventory.pending_status')}</option>
            <option value="approved">{t('inventory.approved_status')}</option>
            <option value="ordered">{t('inventory.ordered_status')}</option>
            <option value="received">{t('inventory.received_status')}</option>
            <option value="cancelled">{t('inventory.cancelled_status')}</option>
          </select>
        </div>
      </div>

      {purchaseOrders.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">📋</div>
          <h3>{t('inventory.no_purchase_orders_message')}</h3>
          <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
            <span aria-hidden="true">➕</span>
            {t('inventory.add_purchase_order_button')}
          </button>
        </div>
      ) : (
        <div className="pm-table-wrap">
          <table className="pm-table" role="grid">
            <thead>
              <tr>
                <th style={{ width: '100px' }}>
                  <div className="pm-header-content">
                    {tableColumns.filter(c => !c.visible).length > 0 && (
                      <button
                        className="pm-column-btn visibility-btn"
                        onClick={() => setShowColumnMenu(showColumnMenu === 'hidden' ? null : 'hidden')}
                        title={t('inventory.show_hidden_columns')}
                      >
                        ⊕
                      </button>
                    )}
                    <div className="pm-header-label">{t('inventory.actions_header')}</div>
                  </div>
                  {showColumnMenu === 'hidden' && (
                    <div className="pm-column-menu">
                      <div className="menu-header">{t('inventory.hidden_columns_label')}</div>
                      {tableColumns.filter(c => !c.visible).map(col => (
                        <button key={col.key} onClick={() => toggleColumnVisibility(col.key)}>
                          {t('inventory.show_column')} {col.label}
                        </button>
                      ))}
                      <hr />
                      <button onClick={() => {
                        resetColumnSettings();
                        setShowColumnMenu(null);
                      }}>
                        {t('inventory.reset_columns')}
                      </button>
                    </div>
                  )}
                </th>
                {visibleColumns.map((col, index) => (
                  <th key={col.key} className="pm-sortable-header">
                    <div className="pm-header-content">
                      <div className="pm-header-controls">
                        <button
                          className="pm-column-btn"
                          onClick={() => moveColumnUp(col.key, visibleColumns)}
                          disabled={index === 0}
                          title={t('inventory.move_left_tooltip')}
                        >
                          ◀
                        </button>
                        <button
                          className="pm-column-btn"
                          onClick={() => moveColumnDown(col.key, visibleColumns)}
                          disabled={index === visibleColumns.length - 1}
                          title={t('inventory.move_right_tooltip')}
                        >
                          ▶
                        </button>
                        <button
                          className="pm-column-btn visibility-btn"
                          onClick={() => setShowColumnMenu(showColumnMenu === col.key ? null : col.key)}
                          title={t('inventory.column_options_tooltip')}
                        >
                          ⋮
                        </button>
                      </div>
                      <div className="pm-header-label" onClick={() => handleSort(col.key)}>
                        {col.label}
                        {sortConfig.key === col.key && (
                          <span className="pm-sort-indicator">
                            {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                          </span>
                        )}
                      </div>
                    </div>
                    {showColumnMenu === col.key && (
                      <div className="pm-column-menu">
                        <button onClick={() => {
                          toggleColumnVisibility(col.key);
                          setShowColumnMenu(null);
                        }}>
                          {t('inventory.hide_column')}
                        </button>
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <div className="pm-row-actions">
                      <button
                        className="pm-row-btn"
                        onClick={() => setShowItemsModal(order.id)}
                        title={t('inventory.view_items_tooltip')}
                      >
                        📦
                      </button>
                      <button
                        className="pm-row-btn edit"
                        onClick={() => handleOpenModal(order)}
                        title={t('inventory.edit_tooltip')}
                      >
                        ✏️
                      </button>
                      <button
                        className="pm-row-btn delete"
                        onClick={() => handleDelete(order.id)}
                        title={t('inventory.delete_tooltip')}
                      >
                        🗑️
                      </button>
                      <button
                        className="pm-row-btn"
                        onClick={() => handleApprove(order.id)}
                        title={t('inventory.approve_tooltip')}
                      >
                        ✅
                      </button>
                    </div>
                  </td>
                  {visibleColumns.map(col => (
                    <td key={col.key}>
                      {col.key === 'status' ? (
                        <span className={`pm-status-badge ${order[col.key]?.toString().toLowerCase()}`}>
                          {formatCellValue(order[col.key], col.key)}
                        </span>
                      ) : (
                        formatCellValue(order[col.key], col.key)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="pm-modal-overlay">
          <div className="pm-modal-content">
            <div className="pm-modal-header">
              <h2>{editingId ? t('inventory.edit_purchase_order_title') : t('inventory.add_purchase_order_title')}</h2>
              <button className="pm-modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-form-grid">
                {Object.keys(editFormData).filter(k => k !== 'id' && k !== 'organization').map(key => {
                  const value = editFormData[key] ?? '';
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  const isBoolean = typeof value === 'boolean';
                  const isNumber = key.includes('total') || key.includes('amount') || key.includes('Total') || key.includes('Amount');
                  const isTextarea = key === 'notes' || key === 'description';
                  const isSelect = isSelectField(key);
                  const selectOptions = isSelect ? getSelectOptions(key) : [];

                  if (isBoolean) {
                    return (
                      <div key={key} className="pm-form-group full-width">
                        <label>
                          <input
                            type="checkbox"
                            role="switch"
                            checked={!!value}
                            onChange={(e) => setEditFormData({ ...editFormData, [key]: e.target.checked })}
                          />
                          {label}
                        </label>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className="pm-form-group">
                      <label>{label}</label>
                      {isSelect ? (
                        <select
                          value={value}
                          onChange={(e) => setEditFormData({ ...editFormData, [key]: e.target.value })}
                        >
                          <option value="">{t('inventory.select_placeholder')} {label} --</option>
                          {selectOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : isTextarea ? (
                        <textarea
                          value={value}
                          onChange={(e) => setEditFormData({ ...editFormData, [key]: e.target.value })}
                        />
                      ) : (
                        <input
                          type={isNumber ? 'number' : key.includes('date') || key.includes('Date') ? 'date' : 'text'}
                          step={isNumber ? '0.01' : undefined}
                          value={value}
                          onChange={(e) => setEditFormData({ ...editFormData, [key]: e.target.value })}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn-secondary" onClick={handleCloseModal}>
                {t('inventory.cancel_button')}
              </button>
              <button className="pm-btn-primary" onClick={handleSave}>
                <span aria-hidden="true">💾</span>
                {editingId ? t('inventory.update_button') : t('inventory.create_button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showItemsModal && (
        <PurchaseOrderItems
          purchaseOrderId={showItemsModal}
          organization={organization}
          onClose={() => setShowItemsModal(null)}
        />
      )}
    </div>
  );
};

export default PurchaseOrders;
